import { Injectable } from '@angular/core';
import { OrderMenuApi } from '../api/order-menu.api';
import { ItemDetailsStore } from '../stores/item-details.store';
import {
  ItemModifierGroupUI,
  ItemModifierUI
} from '../pages/v2-order-pay/models/item-modifier-ui.model';
import { IMenuV2ModifierGroup } from '../api/contracts/menu-v2.contracts';
import { RxUtil } from 'utility/rx.util';
import { NestedModifierGroupSheet } from '../pages/v2-order-pay/bottom-sheets/nested-modifier-group-sheet/nested-modifier-group.sheet';
import { BottomSheetService } from '../v2-components/services/bottom-sheet.service';
import { NestedModGroupSheetStore } from 'stores/nested-mod-group-sheet.store';

interface NestedItemModifierGroupUI extends ItemModifierGroupUI {
  parentMod?: ItemModifierUI;
}
@Injectable({
  providedIn: 'root'
})
export class ModifierGroupService {
  private readonly defaultMaxQty = 999;

  constructor(
    private orderMenuApi: OrderMenuApi,
    private itemDetailsStore: ItemDetailsStore,
    private bottomSheetService: BottomSheetService,
    private nestedModGroupSheetStore: NestedModGroupSheetStore
  ) {}

  async loadModifierGroupsForSelectedItem(): Promise<void> {
    const selectedItemId = (
      await RxUtil.takeOne(this.itemDetailsStore.selectedItem$)
    ).id;
    this.itemDetailsStore.modifierGroupsForSelectedItem =
      this.mapMenuV2ModifierGroups(
        await this.orderMenuApi.getMenuV2ModifierGroups(selectedItemId)
      );
  }

  private mapMenuV2ModifierGroups(
    modifierGroups: IMenuV2ModifierGroup[],
    ownershipChainKey?: string
  ): ItemModifierGroupUI[] {
    return modifierGroups.map((mg) => {
      const groupId = mg._id;
      const passDownKey = ownershipChainKey
        ? `${ownershipChainKey}-${groupId}`
        : groupId;

      return {
        id: groupId,
        ownershipChainKey: passDownKey,
        title: (mg.displayName || mg.name) ?? '',
        information: '',
        minimum: mg.constraints?.min ?? 0,
        maximum: mg.constraints?.max ?? 0,
        modifiers: (mg.options ?? []).map((m) => {
          const modOwnershipKey = `${passDownKey}-${m.itemId}`;
          return {
            id: m.itemId,
            ownershipChainKey: modOwnershipKey,
            name: m.displayName ?? '',
            description: m.description,
            price: m.price ?? 0,
            disabled: false,
            isSelected: false,
            allowComments: false,
            commentText: '',
            quantity: 0,
            maximumQty: m.constraints?.max || this.defaultMaxQty,
            inStock: m.inStock ?? true,
            alcohol: {
              containsAlcohol: m.alcohol?.containsAlcohol ?? false
            },
            isDefault: m.isDefault ?? false,
            modifierGroups: m.nestedGroups
              ? this.mapMenuV2ModifierGroups(m.nestedGroups, modOwnershipKey)
              : [],
            isUnavailableDueToAlcohol: false
          };
        }),
        isSelectionValid: false,
        allowMultiplesOfEachOption: mg.allowMultiplesOfEachOption ?? false,
        guestInstructions: mg.guestInstructions ?? ''
      };
    });
  }

  stdGroupTrackByFn(i: number, group: ItemModifierGroupUI): string {
    return group.ownershipChainKey;
  }

  flattenModifierGroupsToModifiers(
    groups: ItemModifierGroupUI[]
  ): ItemModifierUI[] {
    return groups
      .map((g) =>
        g.modifiers.map((m) => [
          m,
          ...this.flattenModifierGroupsToModifiers(m.modifierGroups)
        ])
      )
      .flat(2);
  }

  async openNestedModifierGroupSheet(
    parentModifier: ItemModifierUI,
    navigatingBackThroughTheStack?: boolean
  ): Promise<void> {
    // close sheet if one is already open for nested mods
    if (this.nestedModGroupSheetStore.currentNestedModSheet) {
      this.nestedModGroupSheetStore.currentNestedModSheet.close();
    }

    if (!navigatingBackThroughTheStack) {
      this.nestedModGroupSheetStore.currentModifierGroupSheetParentModifier =
        parentModifier;
    }

    this.nestedModGroupSheetStore.currentNestedModSheet =
      this.bottomSheetService.showBottomSheet(NestedModifierGroupSheet);
  }

  getSelectedNestedModifierGroups(
    modifier?: ItemModifierUI
  ): NestedItemModifierGroupUI[] {
    let result: NestedItemModifierGroupUI[] = [];
    if (modifier) {
      result = this.itemDetailsStore.flattenModifierGroups(
        this.filterSelectedGroups(modifier)
      );
    }
    return result;
  }

  private filterSelectedGroups(
    mod: ItemModifierUI
  ): NestedItemModifierGroupUI[] {
    let groups = mod.modifierGroups;
    return groups
      .filter((group) => group.modifiers.some((mod) => mod.isSelected))
      .map((group) => ({
        ...group,
        parentMod: mod,
        modifiers: this.filterSelectedMods(group.modifiers)
      }));
  }

  private filterSelectedMods(mods: ItemModifierUI[]): ItemModifierUI[] {
    return mods
      .filter((mod) => mod.isSelected)
      .map((mod) => ({
        ...mod,
        modifierGroups: this.filterSelectedGroups(mod)
      }));
  }
}
